import { useState } from 'react'
import React from 'react'
import Typography from '@mui/material/Typography'
import TextField from '@mui/material/TextField'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemText from '@mui/material/ListItemText'
import Paper from '@mui/material/Paper'
import InputAdornment from '@mui/material/InputAdornment'
import SearchIcon from '@mui/icons-material/Search'
import ClearIcon from '@mui/icons-material/Clear'
import IconButton from '@mui/material/IconButton'
import useStyles from '../_styles'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import Button from '@mui/material/Button'
import { EmptySearchRequisIcon } from '../../../../assets/icons/icons'
import useCurrentHcs from '../../../../hooks/useCurrentHcs'
import { useGetIdsHealthcareservicesHcsIdRequiredExpertsForAct } from '@toktokdoctech/tokapi-client'
import { useDebouncedCallback } from '@workspace/frontend-kit'
import { RequiredStepProp } from '../_props'
import { isExpertUnavailableForToday } from '../../../Patient/History/_utils'
import dayjs from 'dayjs'

export default function RequiredStep({
    onDataChange,
    onSelectAndContinue,
}: RequiredStepProp & { onSelectAndContinue?: () => void }) {
    const classes = useStyles()
    const currentHcs = useCurrentHcs()
    const [searchTerm, setSearchTerm] = useState('')
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('')
    const [selectedRequiredExpertId, setSelectedRequiredExpertId] = useState<string | null>(null)

    const debouncedSearch = useDebouncedCallback((value: string) => {
        setDebouncedSearchTerm(value)
    }, 300)

    const { data: requiredExpertsData, isLoading: isRequiredExpertsLoading } =
        useGetIdsHealthcareservicesHcsIdRequiredExpertsForAct(currentHcs.id, {
            search: debouncedSearchTerm || undefined,
            skip: 0,
            limit: 50,
            sortOrder: 'Asc',
        })

    const filteredRequiredExperts = requiredExpertsData?.items ?? []
    const hasSearchTerm = debouncedSearchTerm.length > 0

    const handleResetSearch = () => {
        setSearchTerm('')
        setDebouncedSearchTerm('')
        debouncedSearch('')
    }

    const handleSelectRequiredExpert = (expert: (typeof filteredRequiredExperts)[number]) => {
        const unavailable = isExpertUnavailableForToday(expert.unavailabilityPeriods)
        if (unavailable) return

        setSelectedRequiredExpertId(expert.id)
        onDataChange?.({
            requiredExpertId: expert.id,
            requiredExpertLabel: expert.fullName,
            profession: expert.professions?.[0]?.name,
            speciality: expert.speciality?.name,
            isHcsEmployee: !!expert.isHcsEmployee,
        })
        onSelectAndContinue?.()
    }

    const renderContent = () => {
        if (isRequiredExpertsLoading) {
            return (
                <div className={classes.noResults}>
                    <Typography>Chargement...</Typography>
                </div>
            )
        }
        if (filteredRequiredExperts.length === 0) {
            return (
                <div className={classes.noResults}>
                    <EmptySearchRequisIcon className={classes.searchRequesterIcon} />
                    <Typography className={classes.noPatient}>
                        Désolé, aucun requis n’a été trouvé. Vérifiez l’orthographe du nom, puis réessayez.
                    </Typography>
                    <Button className={classes.resetFilter} onClick={handleResetSearch}>
                        Réinitialiser la recherche
                    </Button>
                </div>
            )
        }

        return (
            <>
                {hasSearchTerm && (
                    <div className={classes.resultsCount}>
                        <Typography variant="body2">
                            {filteredRequiredExperts.length} résultat{filteredRequiredExperts.length > 1 ? 's' : ''}{' '}
                            pour "{debouncedSearchTerm}"
                        </Typography>
                    </div>
                )}
                <List>
                    {filteredRequiredExperts.map((expert) => {
                        const unavailable = isExpertUnavailableForToday(expert.unavailabilityPeriods)

                        return (
                            <React.Fragment key={expert.id}>
                                <ListItem disablePadding>
                                    <ListItemButton
                                        onClick={() => handleSelectRequiredExpert(expert)}
                                        className={`
                      ${selectedRequiredExpertId === expert.id ? classes.selectedPatient : ''}
                      ${unavailable ? classes.disabledPatient : ''}
                    `}
                                    >
                                        <ListItemText
                                            primary={expert.fullName}
                                            secondary={[
                                                expert.professions?.map((p) => p.name).join(', '),
                                                expert.speciality?.name,
                                            ]
                                                .filter(Boolean)
                                                .join(' - ')}
                                            primaryTypographyProps={{ className: classes.patientName }}
                                            secondaryTypographyProps={{ className: classes.patientInfo }}
                                        />
                                        {unavailable && (
                                            <div className={classes.unavailableWrapper}>
                                                <div className={classes.unavailableStatus}>
                                                    <span className={classes.unavailableDot} />
                                                    <Typography className={classes.unavailableLabel}>
                                                        Indisponible
                                                    </Typography>
                                                </div>
                                                <Typography className={classes.unavailableDate}>
                                                    Jusqu'au{' '}
                                                    {dayjs(
                                                        expert.unavailabilityPeriods
                                                            ?.map((p) => p.to)
                                                            .sort()
                                                            .at(-1),
                                                    ).format('DD/MM/YYYY')}{' '}
                                                    inclus
                                                </Typography>
                                            </div>
                                        )}
                                        {selectedRequiredExpertId === expert.id && (
                                            <CheckCircleIcon className={classes.checkIcon} />
                                        )}
                                    </ListItemButton>
                                </ListItem>
                            </React.Fragment>
                        )
                    })}
                </List>
            </>
        )
    }

    return (
        <div className={classes.stepContent}>
            <div style={{ display: 'flex', alignItems: 'center', width: '100%', justifyContent: 'space-between' }}>
                <Typography className={classes.patientLabel}>Sélectionnez le requis destinataire</Typography>
            </div>

            <TextField
                fullWidth
                variant="outlined"
                placeholder="Rechercher un requis"
                value={searchTerm}
                onChange={(e) => {
                    const value = e.target.value
                    setSearchTerm(value)
                    debouncedSearch(value)
                }}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <SearchIcon className={classes.clearSearchIcon} />
                        </InputAdornment>
                    ),
                    endAdornment: searchTerm && (
                        <InputAdornment position="end">
                            <IconButton
                                size="small"
                                onClick={handleResetSearch}
                                edge="end"
                                aria-label="Effacer la recherche"
                            >
                                <ClearIcon className={classes.clearSearchIcon} fontSize="small" />
                            </IconButton>
                        </InputAdornment>
                    ),
                }}
            />

            <Paper className={classes.patientList}>{renderContent()}</Paper>
        </div>
    )
}
